// PatternManifest — the canonical persistence shape for any pattern.
//
// VERSION 2 ARCHITECTURE
// ──────────────────────
// At runtime there is ONE pattern kind: an image overlay. Every entity
// stores either a real source URL (user-uploaded photo, generative
// upload, OAuth provider photo) OR a synthetic source descriptor
// (variant + seed) that the lib renders inline.
//
// Source PNGs are produced in three ways:
//
//   • USER-UPLOADED (avatars, market images, outcome images) — the user
//     uploads their photo directly. The "shape" is whatever they
//     uploaded.
//
//   • CLIENT-GENERATED (profile cover images, generative avatars) — at
//     creation time the client calls `generateGenerativePng(variant,
//     seed, …)` to render the variant's brightness field as a smooth
//     grayscale PNG, then uploads that. Variant + seed are LOCKED into
//     the PNG.
//
//   • SYNTHETIC (placeholders for any entity without an upload) — no
//     PNG exists; the manifest carries `{ variant, seed }` and the
//     renderer runs the brightness function inline. **No static PNG
//     assets are committed for placeholders.**
//
// Either way the manifest the lib consumes looks identical:
//
//   { version: 2, source, palette, seed }
//
// PROPAGATION CONTRACT
// ────────────────────
//   ✓ Edit a brand CSS token → every entity updates
//   ✓ Re-tune `PRESET_TOKENS_RAW.brand` step list → every entity updates
//   ✓ Re-tune `DEFAULT_PARAMS.cellSize` (or any other param) → updates
//   ✓ Tweak the mosaic dither algorithm → updates
//   ✓ Change `DEFAULT_OUTCOME_STEPS` → every outcome-hue entity updates
//   ✓ Change `CATEGORY_PALETTE_MAP` → every market entity updates
//   ✓ Change `CATEGORY_PLACEHOLDER_SOURCE` → every placeholder market updates
//
// What stays locked per-entity:
//   • The source URL (if uploaded — intrinsic to the asset)
//   • For client-generated covers: the variant + seed inside the PNG
//   • The palette ref the consumer chose at creation
//
// VERSION CONTRACT
// ────────────────
// `MANIFEST_VERSION` is a literal const. Bump when the shape evolves and
// register a migrator in `parsePatternManifest`.
//
// V1 → V2 migration: V1 had `sourceUrl: string` directly on the manifest.
// V2 wraps that into `source: { kind: 'url', url }` and adds the
// synthetic source variant. The migrator in parse.ts handles legacy v1
// payloads transparently.

import type { OutcomeHueName, PaletteName } from '../presets';
import type { GenerativeVariant } from '../types';

export const MANIFEST_VERSION = 2 as const;
export type ManifestVersion = typeof MANIFEST_VERSION;

/**
 * Symbolic palette reference. The lib resolves this to actual hex values
 * at render time, so a change to the underlying preset tokens / outcome
 * step list / category palette map / brand CSS vars propagates to every
 * entity that holds this reference.
 *
 * - `preset`: a named brand palette from `PALETTE_NAMES` (avatars, covers).
 * - `outcome-hue`: a deterministic outcome hue (outcome images).
 * - `category`: a market category id; resolves via `CATEGORY_PALETTE_MAP`
 *   to one of the brand presets. Lets a category hue tweak propagate to
 *   every market in that category at once.
 */
export type PaletteRef =
  | { readonly kind: 'preset'; readonly name: PaletteName }
  | { readonly kind: 'outcome-hue'; readonly hue: OutcomeHueName }
  | { readonly kind: 'category'; readonly category: string };

/**
 * Manifest source — either a fetchable URL or a code-synthesised
 * brightness field.
 *
 * - `url`: HTTPS / IPFS URL of a PNG. Lib fetches, samples brightness,
 *   formats with palette.
 * - `synthetic`: lib calls `getBrightnessFn(variant)` directly with the
 *   given seed; no fetch. Used for placeholders so we never need to ship
 *   static PNG assets in the repo.
 */
export type ManifestSource =
  | { readonly kind: 'url'; readonly url: string }
  | { readonly kind: 'synthetic'; readonly variant: GenerativeVariant; readonly seed: number };

/**
 * The canonical pattern manifest. One shape for every entity — whether
 * the source PNG was uploaded by the user, generated by us, or
 * synthesised at render time, the lib treats them uniformly at format
 * time.
 */
export interface PatternManifest {
  readonly version: ManifestVersion;
  readonly source: ManifestSource;
  readonly palette: PaletteRef;
  readonly seed: number;
}
